// src/pages/VehicleV2/VehicleFuel.jsx

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaArrowLeft, FaTimes } from "react-icons/fa";

const TYPE_API = "https://nissi-india.com/api/fuelType";
const RATE_API = "https://nissi-india.com/api/fuelRate";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const dateToYMD = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const VehicleFuel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const urlMode = toStr(searchParams.get("mode"), "type").toLowerCase();
  const fuelTypeId = toInt(searchParams.get("fuelTypeId"), 0);
  const fuelRateId = toInt(searchParams.get("fuelRateId"), 0);

  const [mode, setMode] = useState(urlMode === "rate" || fuelRateId > 0 ? "rate" : "type");
  const isType = mode === "type";
  const isEdit = isType ? fuelTypeId > 0 : fuelRateId > 0;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const canAdd = useMemo(() => {
    const key = isType ? "fueltype_addrights" : "fuelrate_addrights";
    const v = sessionStorage.getItem(key);
    if (v === null) return true;
    return v === "1" || v === "true";
  }, [isType]);

  const branchActive = useMemo(() => {
    const v = sessionStorage.getItem("BranchActive") || localStorage.getItem("BranchActive");
    if (v === null) return true;
    return v === "1" || v === "true";
  }, []);

  const [loading, setLoading] = useState(false);
  const [fuelTypeName, setFuelTypeName] = useState("");
  const [fuelTypes, setFuelTypes] = useState([]);
  const [fuelTypeIdSel, setFuelTypeIdSel] = useState("Select");
  const [rate, setRate] = useState("");
  const [effectDate, setEffectDate] = useState("");

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;
  const labelClass = `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const loadFuelTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${RATE_API}/fuel-types`, { headers: commonHeaders });
      setFuelTypes(Array.isArray(data?.fuelTypes) ? data.fuelTypes : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load fuel types");
      setFuelTypes([]);
    }
  }, [commonHeaders]);

  const loadType = useCallback(async () => {
    if (!fuelTypeId) return;
    const { data } = await axios.get(`${TYPE_API}/view`, {
      params: { id: fuelTypeId },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load fuel type");
    const row = data?.data || data?.row || {};
    setFuelTypeName(row?.fld_fueltypename || row?.fuelTypeName || row?.fld_fueltype || "");
  }, [fuelTypeId, commonHeaders]);

  const loadRate = useCallback(async () => {
    if (!fuelRateId) return;
    const { data } = await axios.get(`${RATE_API}/view`, {
      params: { fuelRateId },
      headers: commonHeaders,
    });
    if (!data?.success) throw new Error(data?.message || "Failed to load fuel rate");
    const d = data?.data || {};
    setFuelTypeIdSel(d.fuelTypeId != null ? String(d.fuelTypeId) : "Select");
    setRate(d.rate != null ? String(d.rate) : "");
    setEffectDate(dateToYMD(d.effectDate || d.date));
  }, [fuelRateId, commonHeaders]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (mode === "rate") await loadFuelTypes();
        if (mode === "type" && fuelTypeId) await loadType();
        if (mode === "rate" && fuelRateId) await loadRate();
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [mode, fuelTypeId, fuelRateId, loadFuelTypes, loadType, loadRate]);

  const handleClear = () => {
    setFuelTypeName("");
    setFuelTypeIdSel("Select");
    setRate("");
    setEffectDate("");
  };

  const handleSave = async () => {
    if (!branchActive || !canAdd) {
      toast.error("Save not allowed");
      return;
    }

    try {
      setLoading(true);

      if (isType) {
        const name = toStr(fuelTypeName);
        if (!name) return toast.error("Please enter Fuel Type");
        const { data } = await axios.post(
          `${TYPE_API}/save`,
          { fuelTypeId: fuelTypeId || 0, fuelTypeName: name },
          { headers: commonHeaders }
        );
        if (!data?.success) throw new Error(data?.message || "Save failed");
        toast.success(data?.message || "Saved successfully");
      } else {
        if (String(fuelTypeIdSel) === "Select") return toast.error("Please select a fuel type");
        if (!toStr(rate)) return toast.error("Please enter rate");
        const { data } = await axios.post(
          `${RATE_API}/save`,
          {
            fuelRateId: fuelRateId || 0,
            fuelTypeId: toInt(fuelTypeIdSel, 0),
            rate: toStr(rate),
            effectDate: effectDate || "",
            loginUserId,
          },
          { headers: commonHeaders }
        );
        if (!data?.success) throw new Error(data?.message || "Save failed");
        toast.success(data?.message || "Saved successfully");
      }

      navigate("/vehicle/VehicleFuelListing");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE FUEL</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {isEdit
                ? isType
                  ? "Edit Fuel Type"
                  : "Edit Fuel Rate"
                : isType
                ? "Add Fuel Type"
                : "Add Fuel Rate"}
            </h2>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate("/vehicle/VehicleFuelListing")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft /> Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                } ${buttonClass} ${hoverClass}`}
              >
                <FaSave />
                {loading ? "Saving..." : isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>

          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              Entry Type
            </h3>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={mode === "type"}
                  disabled={isEdit}
                  onChange={() => setMode("type")}
                  className="w-4 h-4 text-blue-600"
                />
                Fuel Type
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={mode === "rate"}
                  disabled={isEdit}
                  onChange={() => {
                    setMode("rate");
                    loadFuelTypes();
                  }}
                  className="w-4 h-4 text-blue-600"
                />
                Fuel Rate
              </label>
            </div>
          </div>

          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              {isType ? "Fuel Type Details" : "Fuel Rate Details"}
            </h3>

            {isType ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Fuel Type *</label>
                  <input
                    value={fuelTypeName}
                    onChange={(e) => setFuelTypeName(e.target.value)}
                    className={inputClass}
                    placeholder="Enter fuel type..."
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>Fuel Type *</label>
                  <select
                    value={fuelTypeIdSel}
                    onChange={(e) => setFuelTypeIdSel(e.target.value)}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {fuelTypes.map((x) => (
                      <option key={x.id} value={String(x.id)}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Rate *</label>
                  <input
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                    className={inputClass}
                    placeholder="Enter rate..."
                  />
                </div>
                <div>
                  <label className={labelClass}>Effect Date</label>
                  <input
                    type="date"
                    value={effectDate}
                    onChange={(e) => setEffectDate(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleClear}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                isDark
                  ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaTimes /> Clear
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
            >
              <FaSave />
              {isEdit ? "Update" : "Save"}
            </button>
          </div>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleFuel;